import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import tokenStore from '@/utils/tokenStore';
import { getApiUrl } from '@/utils/apiUrl';

// Logger that suppresses in production
const logger = {
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(tokenStore.getToken());
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const refreshTimerRef = useRef(null);

  const API = getApiUrl();

  const logout = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tokenStore.clear();
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    // Clear httpOnly cookies via backend
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleForcedLogout = () => logout();
    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, [logout]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const delay = Math.max((expiresAt - Date.now()) - 5 * 60 * 1000, 30000);
      refreshTimerRef.current = setTimeout(async () => {
        const rt = tokenStore.getRefreshToken();
        if (!rt) return;
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: rt });
          tokenStore.setToken(data.access_token);
          if (data.refresh_token) tokenStore.setRefreshToken(data.refresh_token);
          setToken(data.access_token);
          scheduleRefresh(data.access_token);
        } catch (err) {
          logger.warn('Token refresh failed', err);
        }
      }, delay);
    } catch (err) {
      logger.warn('Invalid token format for schedule', err);
    }
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
      scheduleRefresh(token);
    } else {
      setLoading(false);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      if (response.data?.must_change_password) {
        setMustChangePassword(true);
      }
    } catch (error) {
      if (error?.cancelled || error?.type === 'network' || error?.type === 'timeout') return;
      if (error?.status === 401 && error?.type === 'auth') {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, refresh_token, must_change_password } = response.data;
      tokenStore.setToken(access_token);
      tokenStore.setRefreshToken(refresh_token);

      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
      setToken(access_token);
      scheduleRefresh(access_token);

      if (must_change_password || userResponse.data?.must_change_password) {
        setMustChangePassword(true);
      }
      return response.data;
    } catch (error) {
      // Provide clear error messages
      if (error?.type === 'network') {
        throw new Error('Impossible de se connecter au serveur. Verifiez votre connexion internet.');
      }
      if (error?.response?.status === 401) {
        throw new Error('Identifiants invalides');
      }
      throw new Error(error?.response?.data?.detail || error?.message || 'Erreur de connexion');
    }
  };

  const passwordChanged = () => {
    setMustChangePassword(false);
    setUser(prev => prev ? { ...prev, must_change_password: false } : prev);
  };

  const verify2FA = async () => { return; };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const value = {
    user, token, loading, login, verify2FA, register, logout,
    isAuthenticated: !!token && !!user,
    mustChangePassword, passwordChanged
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
